import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { GraduationCap, Sparkles, User2, LogOut, Crown, Zap, Radar, ShoppingBag, Bell, BookText, Coins, List as ListIcon, Landmark, Vote } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "../i18n/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { ASSETS, LINKS } from "../assets";
import { useSimulatorI18n } from "../i18n/simulator";
import { trackCta } from "../lib/analytics";
import { useCart } from "../contexts/CartContext";
import { useShopI18n } from "../i18n/shop";
import { NotificationBell } from "./shadow/NotificationBell";
import { RebelBalancePill } from "./shadow/RebelBalancePill";
import { MessagesBell } from "./shadow/MessagesBell";
import { tShadow } from "../i18n/shadow-social";
import ShadowOnboardingModal from "./shadow/ShadowOnboardingModal";
// Runtime-first API base — see /lib/api.js. Using REACT_APP_BACKEND_URL
// directly bakes the preview URL into the production bundle and makes the
// "Ver perfil" link silently fall back to /shadow/perfil/editar because the
// cross-origin GET to fetch the user's username gets blocked by CORS.
import { API_BASE as API } from "../lib/api";

const ONBOARDING_DISMISS_KEY = "rebel:shadow-onboarding-dismissed";

// NavVaultLink — "VAULT · ~X% APY" pill (only for authenticated users).
const NavVaultLink = () => {
  const [apy, setApy] = useState(null);
  useEffect(() => {
    axios.get(`${API}/api/vault/stats`).then((r) => setApy(r.data?.estimated_apy ?? null)).catch(() => {});
  }, []);
  return (
    <Link
      to="/vault"
      data-testid="nav-vault-link"
      className="group relative hidden min-h-[44px] shrink-0 items-center gap-2 justify-center overflow-hidden rounded-full border border-emerald-400/40 bg-black/40 px-3 py-2.5 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-200 backdrop-blur-md lg:flex-1 lg:max-w-[180px] 2xl:text-[11px] transition-all hover:-translate-y-0.5 hover:border-emerald-400/70 hover:bg-emerald-400/[0.08] lg:inline-flex"
    >
      <Landmark className="h-4 w-4 shrink-0 text-emerald-300 lg:max-xl:hidden" strokeWidth={2.5} />
      <span className="hidden lg:inline">VAULT</span>
      {apy != null && <span className="hidden 2xl:inline">· ~{apy}% APY</span>}
    </Link>
  );
};

// NavDaoLink — "DAO" pill with active-proposal badge (visible to everyone).
const NavDaoLink = () => {
  const [active, setActive] = useState(0);
  useEffect(() => {
    axios.get(`${API}/api/governance/stats`).then((r) => setActive(r.data?.activeProposals ?? 0)).catch(() => {});
  }, []);
  return (
    <Link
      to="/governance"
      data-testid="nav-dao-link"
      className="group relative hidden min-h-[44px] shrink-0 items-center gap-2 justify-center overflow-hidden rounded-full border border-purple-400/40 bg-black/40 px-3 py-2.5 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-purple-200 backdrop-blur-md lg:flex-1 lg:max-w-[180px] 2xl:text-[11px] transition-all hover:-translate-y-0.5 hover:border-purple-400/70 hover:bg-purple-400/[0.08] lg:inline-flex"
    >
      <Vote className="h-4 w-4 shrink-0 text-purple-300 lg:max-xl:hidden" strokeWidth={2.5} />
      <span className="hidden lg:inline">DAO</span>
      {active > 0 && (
        <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-purple-400 px-1 font-mono text-[9px] font-black text-black" data-testid="nav-dao-badge">
          {active}
        </span>
      )}
    </Link>
  );
};

export const Navbar = () => {
  const { t, lang } = useLanguage();
  const tSim = useSimulatorI18n(lang);
  const tShop = useShopI18n(lang);
  const { user, isPro, openLogin, logout } = useAuth();
  const cart = useCart();
  const cartCount = cart?.compute(isPro)?.itemCount || 0;
  const [bumpKey, setBumpKey] = useState(0);
  useEffect(() => {
    if (!cart?.bump) return;
    setBumpKey((k) => k + 1);
  }, [cart?.bump]);
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [socialProfile, setSocialProfile] = useState(null);
  const [onboardingOpen, setOnboardingOpen] = useState(false);

  // Lazy-load social profile (username + profile_complete) once user is authed.
  useEffect(() => {
    if (!user || user === false) {
      setSocialProfile(null);
      return;
    }
    let mounted = true;
    axios
      .get(`${API}/api/shadow/social/profile/me`)
      .then(({ data }) => {
        if (mounted) setSocialProfile(data);
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, [user]);

  const socialIncomplete =
    !!user && user !== false && socialProfile && socialProfile.profile_complete === false;
  const socialUsername = socialProfile?.user?.username;
  const profileHref = socialUsername
    ? `/shadow/perfil/${socialUsername}`
    : "/shadow/perfil/editar";

  // Auto-open onboarding once per session if profile incomplete.
  // Suppress on /rebel-by-eddy so the eddy_admin can edit its own landing
  // without the operator-profile modal blocking clicks.
  useEffect(() => {
    if (!socialIncomplete) return;
    if (typeof window !== "undefined" && window.location?.pathname?.startsWith("/rebel-by-eddy")) return;
    let dismissed = false;
    try {
      dismissed = window.sessionStorage.getItem(ONBOARDING_DISMISS_KEY) === "1";
    } catch {
      /* private mode */
    }
    if (!dismissed) {
      const t = setTimeout(() => setOnboardingOpen(true), 1200);
      return () => clearTimeout(t);
    }
  }, [socialIncomplete]);

  const handleOnboardingClose = async (reason) => {
    setOnboardingOpen(false);
    if (reason === "dismissed") {
      try {
        window.sessionStorage.setItem(ONBOARDING_DISMISS_KEY, "1");
      } catch {
        /* private mode */
      }
    }
    if (reason === "completed") {
      // Refetch profile so the amber dot disappears
      try {
        const { data } = await axios.get(`${API}/api/shadow/social/profile/me`);
        setSocialProfile(data);
      } catch {
        /* noop */
      }
    }
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const [burgerOpen, setBurgerOpen] = useState(false);
  useEffect(() => {
    const onMenu = (e) => setBurgerOpen(!!e.detail?.open);
    window.addEventListener("rebel:menu-state", onMenu);
    return () => window.removeEventListener("rebel:menu-state", onMenu);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled
          ? "nav-scrolled border-b border-white/5 bg-black/60 backdrop-blur-2xl"
          : "bg-transparent"
      }`}
      data-testid="main-navbar"
    >
      <div className="mx-auto flex max-w-[1600px] items-center justify-between gap-1 px-2 py-3 sm:gap-3 sm:px-4 md:gap-2 md:px-4 md:py-4 lg:gap-4 lg:px-6 lg:max-xl:gap-2 lg:max-xl:px-3 xl:px-8">
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="group flex shrink-0 items-center gap-3"
          data-testid="nav-rebel-logo-btn"
        >
          <img
            src={ASSETS.logos.rebel}
            alt="REBEL"
            className="h-8 w-auto transition-transform group-hover:scale-105 md:h-11 lg:max-xl:h-9"
          />
        </button>

        <nav className="hidden flex-1 items-center justify-evenly gap-2 md:flex lg:gap-3">
          {/* Market Simulator pill button — NEW */}
          <Link
            to="/simulator"
            data-testid="nav-simulator-link"
            onClick={() => { try { trackCta("nav_simulator", { location: "navbar" }); } catch {} }}
            className="group relative inline-flex min-h-[44px] shrink-0 items-center justify-center gap-2 overflow-hidden rounded-full border border-emerald-400/40 bg-white/[0.03] px-3 py-2.5 text-[11px] font-bold uppercase tracking-wider text-emerald-100 lg:flex-1 lg:max-w-[180px] shadow-[0_0_18px_rgba(0,230,118,0.14)] backdrop-blur-md transition-all hover:-translate-y-0.5 hover:border-emerald-400/70 hover:bg-emerald-400/10 hover:text-white hover:shadow-[0_0_30px_rgba(0,230,118,0.35)]"
          >
            <Zap className="h-4 w-4 shrink-0 text-emerald-300 lg:max-xl:hidden" strokeWidth={2.5} />
            <span className="hidden lg:inline">SIM</span>
            <span className="ml-1 hidden items-center rounded-full bg-emerald-400 px-1.5 py-0.5 text-[8px] font-black uppercase tracking-widest text-black xl:inline-flex">
              {tSim.nav_badge}
            </span>
            <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-emerald-300/35 to-transparent transition-transform duration-1000 group-hover:translate-x-full" />
          </Link>

          {/* SHADOW NETWORK — premium operator gateway */}
          <Link
            to="/shadow"
            data-testid="nav-shadow-link"
            onClick={() => { try { trackCta("nav_shadow", { location: "navbar" }); } catch {} }}
            className="group relative hidden min-h-[44px] shrink-0 items-center gap-2 justify-center overflow-hidden rounded-full border border-amber-400/40 bg-black/40 px-3 py-2.5 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-amber-200 shadow-[0_0_18px_rgba(251,191,36,0.18)] lg:flex-1 lg:max-w-[180px] 2xl:text-[11px] backdrop-blur-md transition-all hover:-translate-y-0.5 hover:border-amber-400/70 hover:bg-amber-400/[0.08] hover:text-amber-100 hover:shadow-[0_0_32px_rgba(251,191,36,0.45)] lg:inline-flex"
          >
            <Radar className="h-4 w-4 shrink-0 text-amber-300 lg:max-xl:hidden" strokeWidth={2.5} />
            <span className="hidden lg:inline">SHADOW</span>
            <span className="relative ml-1 inline-flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400/80" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-amber-400" />
            </span>
            <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-amber-300/35 to-transparent transition-transform duration-1000 group-hover:translate-x-full" />
          </Link>

          {/* R₿L VAULT — revenue-sharing yield (logged-in users only) */}
          {user && <NavVaultLink />}

          {/* DAO — governance (visible to everyone) */}
          <NavDaoLink />
        </nav>

        <div className="flex shrink-0 items-center gap-1 lg:gap-2.5 lg:max-xl:gap-1">
          {/* ACADEMIA RE₿EL CTA — hidden on mobile (lives inside the burger menu instead) */}
          <Link
            to={LINKS.academy}
            data-testid="nav-academy-btn"
            onClick={() => { try { trackCta("nav_academy", { location: "navbar" }); } catch {} }}
            className="group relative hidden min-h-[44px] shrink-0 items-center gap-2 overflow-hidden rounded-full border border-orange-400/40 bg-gradient-to-r from-orange-500/90 via-amber-500/90 to-orange-600/90 px-3 py-2 text-xs font-bold uppercase tracking-wider text-black shadow-[0_0_20px_rgba(255,140,0,0.35)] transition-all hover:-translate-y-0.5 hover:shadow-[0_0_35px_rgba(255,140,0,0.65)] md:inline-flex md:px-4 md:py-2.5 md:text-[11px]"
          >
            <GraduationCap className="h-4 w-4 shrink-0" strokeWidth={2.5} />
            <span className="hidden 2xl:inline">{t.nav.academy}</span>
            <span className="2xl:hidden lg:max-xl:hidden">RE₿EL</span>
            <span className="relative ml-0.5 inline-flex h-1.5 w-1.5 shrink-0">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-700 opacity-90" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-600" />
            </span>
            <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/50 to-transparent transition-transform duration-1000 group-hover:translate-x-full" />
            <Sparkles className="pointer-events-none absolute -right-1 -top-1 h-3 w-3 text-yellow-100 opacity-70" />
          </Link>

          <LanguageSwitcher />

          {/* Cluster separator (Personal → Economía) — subtle vertical divider. */}
          <span aria-hidden="true" className="mx-0.5 hidden h-5 w-px shrink-0 bg-white/10 md:inline-block" data-testid="navbar-cluster-sep-1" />

          {/* SHADOW MARKET cart trigger — additive, matches site aesthetic */}
          <button
            type="button"
            onClick={() => cart?.openCart()}
            aria-label={tShop.open_cart}
            data-testid="nav-shop-btn"
            className="relative inline-flex min-h-[44px] shrink-0 items-center gap-1.5 rounded-full border border-white/15 bg-white/[0.04] px-2 py-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-white/80 backdrop-blur-md transition-all hover:-translate-y-0.5 hover:border-orange-400/50 hover:text-orange-200 md:gap-2 md:px-3 md:py-2 md:text-xs md:tracking-wider"
          >
            <ShoppingBag key={bumpKey} className={`h-3.5 w-3.5 ${bumpKey ? "cart-bump" : ""}`} />
            <span className="hidden xl:inline">{tShop.nav_shop}</span>
            {cartCount > 0 && (
              <span
                className="absolute -right-1.5 -top-1.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-gradient-to-br from-orange-500 to-amber-600 px-1 text-[9px] font-black text-black shadow-[0_0_10px_rgba(255,140,0,0.6)]"
                data-testid="nav-shop-badge"
              >
                {cartCount}
              </span>
            )}
          </button>

          {/* User button */}
          {user && user !== false ? (
            <div className="relative flex shrink-0 items-center gap-0.5 sm:gap-1.5">
              {/* Cluster separator (Economía → Cuenta/Personal-auth) — subtle vertical divider. */}
              <span aria-hidden="true" className="mr-0.5 hidden h-5 w-px shrink-0 bg-white/10 md:inline-block" data-testid="navbar-cluster-sep-2" />
              <MessagesBell />
              <NotificationBell authed />
              <RebelBalancePill />
              <button
                onClick={() => setMenuOpen((o) => !o)}
                data-testid="nav-user-btn"
                className={`group relative inline-flex min-h-[44px] shrink-0 items-center gap-2 rounded-full border py-1 pl-1 pr-1.5 text-xs font-bold transition-all md:pr-3 ${
                  isPro
                    ? "border-amber-400/50 bg-gradient-to-r from-amber-400/15 to-orange-500/15 text-amber-200 shadow-[0_0_18px_rgba(255,180,0,0.25)] hover:border-amber-300 hover:text-amber-100"
                    : "border-white/15 bg-white/[0.04] text-white/80 hover:border-white/30 hover:text-white"
                }`}
              >
                {user.picture ? (
                  <img
                    src={user.picture}
                    alt=""
                    referrerPolicy="no-referrer"
                    className={`h-7 w-7 shrink-0 rounded-full object-cover ring-2 ${
                      isPro ? "ring-amber-400/60" : "ring-white/15"
                    }`}
                  />
                ) : (
                  <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ring-2 ${
                    isPro ? "bg-amber-400/15 ring-amber-400/60" : "bg-white/[0.06] ring-white/15"
                  }`}>
                    {isPro ? <Crown className="h-3.5 w-3.5" /> : <User2 className="h-3.5 w-3.5" />}
                  </span>
                )}
                <span className="hidden max-w-[120px] truncate 2xl:inline">
                  {user.name || user.email}
                </span>
                {socialIncomplete && (
                  <span
                    data-testid="nav-profile-incomplete-dot"
                    title={tShadow("completaPerfil", lang)}
                    style={{
                      position: "absolute",
                      top: 2,
                      left: 2,
                      width: 9,
                      height: 9,
                      borderRadius: "50%",
                      background: "#f5a623",
                      border: "2px solid #07080f",
                      boxShadow: "0 0 6px rgba(245,166,35,0.7)",
                    }}
                  />
                )}
              </button>
              {menuOpen && (
                <div
                  className="absolute right-0 top-full z-50 mt-2 max-h-[calc(100vh-90px)] w-56 overflow-y-auto rounded-xl border border-white/10 bg-zinc-950/95 p-1.5 shadow-2xl backdrop-blur-xl"
                  data-testid="nav-user-menu"
                  onMouseLeave={() => setMenuOpen(false)}
                >
                  <div className="border-b border-white/5 px-3 py-2">
                    <div className="flex items-center gap-2">
                      <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">
                        {isPro ? "PRO · LIFETIME" : "FREE"}
                      </div>
                      {user.google_linked && (
                        <span
                          className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.04] px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-[0.2em] text-white/55"
                          data-testid="nav-google-linked-badge"
                          title={t.auth.google_linked}
                        >
                          <svg viewBox="0 0 48 48" className="h-2.5 w-2.5" aria-hidden="true">
                            <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.6 32.5 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 5.9 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.4-.4-3.5z"/>
                            <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16 18.9 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.9 29.3 5 24 5 16.3 5 9.7 9.3 6.3 14.7z"/>
                            <path fill="#4CAF50" d="M24 44c5.2 0 10-2 13.6-5.2l-6.3-5.3C29.2 35 26.7 36 24 36c-5.2 0-9.6-3.4-11.3-8.1l-6.5 5C9.6 39.7 16.2 44 24 44z"/>
                            <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.2 4.2-4 5.6l6.3 5.3C41.8 35.3 44 30 44 24c0-1.2-.1-2.4-.4-3.5z"/>
                          </svg>
                          Google
                        </span>
                      )}
                    </div>
                    <div className="truncate text-xs font-bold text-white">
                      {user.email}
                    </div>
                  </div>
                  {!isPro && (
                    <Link
                      to={LINKS.academy}
                      onClick={() => setMenuOpen(false)}
                      className="flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-bold text-amber-200 transition-colors hover:bg-amber-400/10"
                      data-testid="nav-upgrade-link"
                    >
                      <Crown className="h-3.5 w-3.5" />
                      {t.nav.upgrade}
                    </Link>
                  )}
                  <Link
                    to={profileHref}
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-bold text-white/80 transition-colors hover:bg-white/5 hover:text-white"
                    data-testid="nav-profile-link"
                  >
                    <User2 className="h-3.5 w-3.5" />
                    {tShadow("verPerfil", lang)}
                  </Link>
                  <Link
                    to="/shadow/perfil/editar"
                    onClick={(e) => {
                      setMenuOpen(false);
                      if (socialIncomplete) {
                        // Open onboarding wizard inline instead of full page nav
                        e.preventDefault();
                        setOnboardingOpen(true);
                      }
                    }}
                    className={`flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-bold transition-colors ${
                      socialIncomplete
                        ? "text-amber-200 hover:bg-amber-400/10"
                        : "text-white/70 hover:bg-white/5 hover:text-white"
                    }`}
                    data-testid="nav-profile-edit-link"
                  >
                    <Sparkles className="h-3.5 w-3.5" />
                    {socialIncomplete
                      ? tShadow("completaPerfil", lang)
                      : tShadow("editarPerfil", lang)}
                  </Link>
                  <Link
                    to="/shadow/alertas"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-bold text-white/70 transition-colors hover:bg-white/5 hover:text-white"
                    data-testid="nav-alerts-link"
                  >
                    <Bell className="h-3.5 w-3.5" />
                    Alertas
                  </Link>
                  <Link
                    to="/shadow/journal"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-bold text-white/70 transition-colors hover:bg-white/5 hover:text-white"
                    data-testid="nav-journal-link"
                  >
                    <BookText className="h-3.5 w-3.5" />
                    Journal
                  </Link>
                  <Link
                    to="/shadow/mentor"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-bold text-violet-200 transition-colors hover:bg-violet-400/10"
                    data-testid="nav-mentor-link"
                  >
                    <Sparkles className="h-3.5 w-3.5" />
                    Mentor IA
                  </Link>
                  {/* Dashboard Eddy — only for the scoped eddy_admin. Independent of require_admin. */}
                  {user.is_eddy_admin && (
                    <Link
                      to="/rebel-by-eddy/dashboard"
                      onClick={() => setMenuOpen(false)}
                      data-testid="nav-eddy-dashboard-link"
                      className="flex items-center gap-2 rounded-lg border border-amber-400/30 bg-amber-500/[0.06] px-3 py-2 text-xs font-bold text-amber-300 transition-colors hover:bg-amber-500/[0.12]"
                    >
                      <Crown className="h-3.5 w-3.5" />
                      Dashboard Eddy
                    </Link>
                  )}
                  {/* Admin Panel — only for the two admin accounts (matches require_admin allowlist). */}
                  {(["rebelbtc@rebelbtc.com", "camilorodriguezgil9@outlook.es"].includes((user.email || "").toLowerCase())) && (
                    <Link
                      to="/admin"
                      onClick={() => setMenuOpen(false)}
                      data-testid="nav-admin-link"
                      className="flex items-center gap-2 rounded-lg border border-[#f97316]/30 bg-[#f97316]/[0.06] px-3 py-2 text-xs font-bold text-[#f97316] transition-colors hover:bg-[#f97316]/[0.12]"
                    >
                      <Crown className="h-3.5 w-3.5" />
                      Admin Panel
                    </Link>
                  )}
                  <button
                    onClick={() => { setMenuOpen(false); logout(); }}
                    data-testid="nav-logout-btn"
                    className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-xs font-bold text-white/70 transition-colors hover:bg-white/5 hover:text-white"
                  >
                    <LogOut className="h-3.5 w-3.5" />
                    {t.nav.logout}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button
              onClick={openLogin}
              data-testid="nav-login-btn"
              className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-white/20 bg-white/[0.06] px-2 py-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-white/90 backdrop-blur-md transition-all hover:-translate-y-0.5 hover:border-white/40 hover:bg-white/[0.1] hover:text-white md:gap-2 md:px-3 md:py-2 md:text-xs md:tracking-wider"
              style={{ animation: "pulse-access 1.8s ease-in-out infinite" }}
              aria-label={t.nav.login}
            >
              <User2 className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">{t.nav.login}</span>
            </button>
          )}

          {/* Effect 3 — Sofi-style menu trigger (placed last so it sits on the far right on mobile) */}
          <button
            type="button"
            aria-label="open menu"
            onClick={() => window.dispatchEvent(new CustomEvent("rebel:toggle-menu"))}
            data-testid="rebel-burger-btn"
            className={`rebel-burger shrink-0 ${burgerOpen ? "is-open" : ""}`}
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </div>
      {onboardingOpen && (
        <ShadowOnboardingModal
          open={onboardingOpen}
          onClose={handleOnboardingClose}
          initialProfile={socialProfile}
        />
      )}
    </motion.header>
  );
};
